// News post: Meet the Bumpups API (October 9, 2026)
import React from 'react';
import { Link } from 'react-router-dom';
import keys_asset from '../../../assets/images/blog/blog-bumpups-api-keys.jpg';
import docs_asset from '../../../assets/images/blog/blog-bumpups-api-docs.jpg';

const BumpupsApi = () => (
  <>
    <p>
      Bumpups Pro now includes the <strong>Bumpups API</strong>. Send a YouTube link from your own code and get
      <strong> timestamped chapters</strong> back, then <strong>ask questions</strong> about the video. It’s the same Bump AI
      you use in the app, with the same minutes, so your scripts, your website and your automations can do what you do in your
      Workspace.
    </p>

    <h2>A YouTube link in, chapters out</h2>
    <p>
      Send one request with a video’s link and Bumpups gets to work. A few minutes later the video is ready, with chapters you
      can paste straight into a YouTube description. We sent a 55-minute Harvard lecture on justice, and it came back with 15
      chapters, from “2:30 Audience poll: trolley dilemma choices” to “29:35 Queen v. Dudley and Stephens case introduced.”
    </p>
    <p>
      Then ask about it. We asked <strong>“Which cases does the professor use to test our moral intuitions?”</strong> and got
      the trolley driver, the onlooker on the bridge, the transplant surgeon and more, each explained in a sentence.
    </p>
    <ul>
      <li><strong>Ready to paste.</strong> Chapters come back as a list with start times, and as text ready for a YouTube description.</li>
      <li><strong>Ask anything.</strong> Get the same answers Bump AI gives in chat, and follow-up questions keep the context.</li>
      <li><strong>Safe to retry.</strong> Send an idempotency key, and a retried request never charges you twice.</li>
      <li><strong>Long videos welcome.</strong> Public YouTube videos up to 3 hours. Shorter videos are usually ready in 1–2 minutes.</li>
    </ul>

    <h2>One account, the same limits</h2>
    <p>
      The API isn’t a separate product with its own bill. It runs on your Pro plan, and everything it does shows up in the app.
      A new <strong>API</strong> page in the sidebar holds your keys, your minutes and your recent API videos.
    </p>
    <figure>
      <img src={keys_asset} alt="The API page: the base URL, minutes left, a key named My server and a recent API video with 1 of 25 questions used" loading="lazy" width="1832" height="1766" />
    </figure>
    <ul>
      <li><strong>Keys in one place.</strong> Name a key, copy it once, and revoke it whenever you like. You can have up to 5.</li>
      <li><strong>The same minutes.</strong> A video costs its length in minutes, from the same balance as the web app. If no chapters come back, you get the minutes back.</li>
      <li><strong>The same chat.</strong> Each video allows 25 questions, shared between the app and the API. API answers appear in a chat named “API” on the video.</li>
      <li><strong>Everything in your Workspace.</strong> Every video you process through the API appears in your Workspace with an API tag, ready to open, chat about and export.</li>
    </ul>

    <h2>Docs you can actually use</h2>
    <p>
      The full reference is public at <Link to="/developers/docs">bumpups.com/developers/docs</Link>. Every endpoint has a
      ready-to-run request in curl, Python or JavaScript next to a real response, and one click copies it. Errors explain
      exactly what went wrong, and an OpenAPI file is there for code generators and AI agents.
    </p>
    <figure>
      <img src={docs_asset} alt="The API reference open at Process a video, with a curl request and the 202 Accepted response" loading="lazy" width="1640" height="1127" />
    </figure>

    <h2>Never miss what’s next</h2>
    <p>
      Every new feature, improvement and fix lives on our <Link to="/changelog">Changelog</Link>.
    </p>
    <p>
      On Pro? Open <strong>API</strong> in the sidebar and create your first key. Not yet? See what the API can do
      at <Link to="/developers">bumpups.com/developers</Link>. We can’t wait to see what you build!
    </p>
  </>
);

export default BumpupsApi;
